import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Mail, Phone, MapPin, Building, Lock, ArrowRight, CheckCircle } from "lucide-react";

export default function Registro() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    rut: "",
    password: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        rut: formData.rut,
        password: formData.password
      });
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl mb-4">¡REGISTRO EXITOSO!</h1>
          <p className="text-muted-foreground mb-6">
            Tu cuenta ha sido creada y está <strong>pendiente de aprobación</strong>. 
            Te notificaremos por email cuando tu cuenta sea verificada y puedas realizar pedidos.
          </p>
          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-sm text-left">
            <p className="font-medium mb-2">¿Por qué necesitamos aprobar tu cuenta?</p>
            <p className="text-muted-foreground">
              Para brindarte un mejor servicio y evitar pedidos fraudulentos, 
              verificamos los datos de cada cliente antes de habilitar su cuenta.
            </p>
          </div>
          <Link to="/">
            <Button className="w-full">
              Volver al Inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl mb-2">CREAR CUENTA</h1>
          <p className="text-muted-foreground">
            Completá tus datos para registrarte
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 md:p-8 space-y-5">
          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Nombre Completo *
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Juan Pérez"
              required
              data-testid="register-name"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="juan@ejemplo.com"
              required
              data-testid="register-email"
            />
          </div>

          {/* Teléfono */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Teléfono / Celular *
            </Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="099 123 456"
              required
              data-testid="register-phone"
            />
          </div>

          {/* Ciudad y Dirección */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Ciudad
              </Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Maldonado"
                data-testid="register-city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rut" className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                Cédula / RUT
              </Label>
              <Input
                id="rut"
                name="rut"
                value={formData.rut}
                onChange={handleChange}
                placeholder="12345678"
                data-testid="register-rut"
              />
            </div>
          </div>

          {/* Dirección */}
          <div className="space-y-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Av. España 1234"
              data-testid="register-address"
            />
          </div>

          {/* Contraseñas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Contraseña *
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 6 caracteres"
                required
                data-testid="register-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repetir contraseña"
                required
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 text-base"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? "Registrando..." : "Crear Cuenta"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            ¿Ya tenés cuenta?{" "}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Iniciá sesión
            </Link>
          </p>
        </form>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Tu cuenta será revisada antes de poder realizar pedidos.
        </p>
      </div>
    </div>
  );
}
